import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, first, map, Observable, Subject, tap } from 'rxjs';
import { AUTH_STRATEGY, AuthStrategy, UpdateUserPayload, User } from '@plys/src/auth/application/auth.strategy';

@Injectable()
export class AuthState {
  private _user = new BehaviorSubject<User>(null);
  private readonly _loginEvent = new Subject<void>();
  private readonly _logoutEvent = new Subject<void>();

  get loginEvent$(): Observable<void> {
    return this._loginEvent.asObservable();
  }

  get logoutEvent$(): Observable<void> {
    return this._logoutEvent.asObservable();
  }

  constructor(@Inject(AUTH_STRATEGY) private authStrategy: AuthStrategy) {}

  get user$(): Observable<User> {
    return this._user.asObservable();
  }

  set user(user: User) {
    this._user.next(user);
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.user$.pipe(map((user) => !!user));
  }

  get isLoggedIn(): boolean {
    return !!this._user.value;
  }

  /**
   * Drops a restored session that does not belong to the shop being opened. Accounts are
   * shop-scoped (every `login` / `register` posts a `shop_id`), and the app boots `getUser()` long
   * before the POS names its shop — so a token left behind by another shop on this origin used to
   * present a logged-in buyer whose every shop-scoped call would then fail. `/me` now reports
   * `shop_ids`, so the mismatch is settled without a request of its own.
   *
   * Deliberately inert when `shop_ids` is absent or empty: only a reported list that excludes this
   * shop is an answer, anything else is "unknown" and the session is left alone.
   */
  dropSessionOutsideShop(shopId: string): void {
    const shopIds = this._user.value?.shopIds;
    if (!shopId || !shopIds?.length || shopIds.includes(shopId)) {
      return;
    }

    this.logout().pipe(first()).subscribe();
  }

  /**
   * `payload` is only what the caller edited. The cached user is deliberately not merged in: it is
   * written at login and nowhere else, so re-sending it either re-judged a number the backend no
   * longer stores or silently reverted a profile change made in the portal (PAUL-3514). The strategy
   * reads the current profile itself, since the write replaces every field.
   */
  updateProfile(payload: UpdateUserPayload): Observable<User> {
    return this.authStrategy.updateUser(payload).pipe(
      tap((user) => {
        this.user = user;
      })
    );
  }

  login(login: string, password: string): Observable<User> {
    return this.authStrategy.login(login, password).pipe(
      tap((user) => {
        this.user = user;
        this._loginEvent.next();
      })
    );
  }

  logout(): Observable<boolean> {
    return this.authStrategy.logout().pipe(
      tap(() => {
        this.user = null;
        this._logoutEvent.next();
      })
    );
  }

  sendPasswordResetEmail(email: string): Observable<boolean> {
    return this.authStrategy.remindPassword(email);
  }

  resetPassword(token: string, password: string, confirmPassword: string): Observable<boolean> {
    return this.authStrategy.resetPassword(token, password, confirmPassword);
  }

  register(
    email: string,
    password: string,
    confirmPassword: string,
    firstname: string,
    lastname: string
  ): Observable<User> {
    return this.authStrategy.register({ email, password, confirmPassword, firstname, lastname }).pipe(
      tap((user) => {
        this.user = user;
        this._loginEvent.next();
      })
    );
  }

  getUser(): Observable<User> {
    return this.authStrategy.getUser().pipe(
      tap((user) => {
        this.user = user;
      })
    );
  }

  setUser(user: User): void {
    this.user = user;
  }

  exists(email: string): Observable<boolean> {
    return this.authStrategy.exists(email);
  }
}
